import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Download, Trash2, Upload } from 'lucide-react';

import type { ProjectDocResponse } from '@/domain/models/project.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { DOC_CATEGORIES } from '@/shared/constants/document-categories.js';
import { formatDate } from '@/shared/utils/format-date.js';
import { formatFileSize } from '@/shared/utils/format-file-size.js';

interface Props {
    documents: ProjectDocResponse[];
    onDownload: (doc: ProjectDocResponse) => void;
    onDelete: (docId: string) => void;
    onUpload: (category: string) => void;
}

export function DocumentsTab({ documents, onDownload, onDelete, onUpload }: Props) {
    const { translate } = useApp();

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                    {translate('projects.tabs.documentos')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {translate('projects.docsDesc')}
                </Typography>
            </Box>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1, overflow: 'hidden' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                                    {translate('projects.docCategory')}
                                </TableCell>
                                <TableCell sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                                    {translate('projects.docFile')}
                                </TableCell>
                                <TableCell sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                                    {translate('projects.docUploadedAt')}
                                </TableCell>
                                <TableCell sx={{ width: 90 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {DOC_CATEGORIES.map((cat) => {
                                const doc = documents.find((d) => d.category === cat.key);
                                return (
                                    <TableRow key={cat.key} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {cat.label}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {doc ? (
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip label={doc.fileName} size="small" sx={{ fontSize: 11 }} />
                                                    {doc.fileSizeBytes && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatFileSize(Number(doc.fileSizeBytes))}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                                    {translate('projects.docNotUploaded')}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.disabled">
                                                {doc ? formatDate(doc.uploadedAt) : '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5}>
                                                {doc ? (
                                                    <>
                                                        <IconButton size="small" aria-label={translate('documents.download')} onClick={() => onDownload(doc)}>
                                                            <Download size={14} />
                                                        </IconButton>
                                                        <IconButton size="small" color="error" aria-label={translate('common.delete')} onClick={() => onDelete(doc.id)}>
                                                            <Trash2 size={14} />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    <IconButton size="small" aria-label={translate('documents.uploadFile')} onClick={() => onUpload(cat.key)}>
                                                        <Upload size={14} />
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Box>
    );
}
